<template>
  <div class="page-container" v-if="currentNews && currentNews.title">
    <h1 class="page-title">{{ currentNews.title }}</h1>
    <p class="page-date" v-if="currentNews.date">{{ currentNews.date }}</p>
    <div class="content">
      <template v-for="(section, index) in contentSections" :key="index">
        <div class="content-section">
          <div class="text-block" v-html="formatSection(section)"></div>
        </div>

        <!-- Insert Top Image after the first paragraph -->
        <div v-if="index === 0 && (currentNews as any).other?.picture1" class="content-section image-section">
          <div class="image-container">
            <img :src="(currentNews as any).other.picture1.url" :alt="(currentNews as any).other.picture1.caption || 'News Image'">
            <p v-if="(currentNews as any).other.picture1.caption" class="image-caption">{{ (currentNews as any).other.picture1.caption }}</p>
          </div>
        </div>

        <!-- Insert Carousel after the "Honors" paragraph (Index 3 for ZH, 7 for EN) -->
        <div v-if="index === carouselIndex && (currentNews as any).other?.carouselImages" class="content-section carousel-section">
          <el-carousel :interval="4000" type="card" height="300px">
            <el-carousel-item v-for="(item, idx) in (currentNews as any).other.carouselImages" :key="idx">
              <div class="carousel-image-container">
                <img :src="item.url" :alt="item.caption || 'Carousel Image'">
                <div v-if="item.caption" class="carousel-caption">{{ item.caption }}</div>
              </div>
            </el-carousel-item>
          </el-carousel>
        </div>

        <!-- Insert Dinner Images (Awards) after the "Awards" paragraph (Index 7 for ZH, 4 for EN) -->
        <div v-if="index === dinnerIndex && (currentNews as any).other?.dinnerImages" class="content-section gallery-section">
          <div class="image-grid">
            <div v-for="(item, idx) in (currentNews as any).other.dinnerImages" :key="idx" class="grid-item">
              <img :src="item.url" :alt="item.caption || 'Awards Image'">
              <p v-if="item.caption" class="grid-caption">{{ item.caption }}</p>
            </div>
          </div>
        </div>

        <!-- Insert Video after the "Dinner" paragraph (Index 8 for ZH) -->
        <div v-if="index === videoIndex && (currentNews as any).other?.video" class="content-section video-section">
          <div class="video-container">
            <video controls width="100%">
              <source :src="(currentNews as any).other.video" type="video/mp4">
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </template>
    </div>
  </div>
  <div v-else class="page-container">
    <p>新闻内容未找到</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useLangStore } from '../../../pinia/stores/lang';
import zhData from '../../../i18n/zh';
import enData from '../../../i18n/en';

const langStore = useLangStore();

const currentNews = computed(() => {
  const translationData = langStore.currentLang.startsWith('zh') ? zhData : enData;
  const newsList = translationData.pages.news.company.news.list;
  const newsItem = newsList.find((news: any) => news.id === 'sst2025summary');
  return newsItem || {};
});

const contentSections = computed(() => {
  const content = (currentNews.value as any)?.content || '';
  return content ? content.split('---').filter((p: string) => p.trim()) : [];
});

const formatSection = (content: string) => {
  if (!content) return '';
  // Replace <br><br> with </p><p> to create proper paragraphs for indentation
  return `<p>${content.replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '</p><p>')}</p>`;
};

const carouselIndex = computed(() => langStore.currentLang.startsWith('zh') ? 5 : 3);
const dinnerIndex = computed(() => langStore.currentLang.startsWith('zh') ? 7 : 4);
const videoIndex = computed(() => langStore.currentLang.startsWith('zh') ? 8 : -1);
</script>

<style scoped>
.page-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Microsoft YaHei', Arial, sans-serif;
}

.page-title {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.4;
  text-align: center;
}

.page-date {
  color: #666;
  font-size: 14px;
  margin-bottom: 30px;
  text-align: center;
}

.content {
  line-height: 1.8;
  color: #444;
}

.content-section {
  margin-bottom: 20px;
}

.text-block :deep(p) {
  margin-bottom: 15px;
  text-align: justify;
  font-size: 16px;
  text-indent: 2em;
}

.text-block :deep(b),
.text-block :deep(strong) {
  color: #1e80ff;
  font-weight: bold;
}

/* Image Section Styles */
.image-section {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
  margin-bottom: 20px;
}

.image-container {
  width: 100%;
  text-align: center;
}

.image-container img {
  max-width: 100%;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.image-caption {
  margin-top: 8px;
  color: #666;
  font-size: 14px;
  font-style: italic;
}

/* Carousel Section Styles */
.carousel-section {
  margin: 40px 0;
}

.carousel-image-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
}

.carousel-image-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carousel-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 8px;
  text-align: center;
  font-size: 14px;
}

/* Gallery Section Styles */
.gallery-section {
  margin: 30px 0;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.grid-item {
  text-align: center;
}

.grid-item img {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
}

.grid-item img:hover {
  transform: scale(1.02);
}

.grid-caption {
  margin-top: 5px;
  font-size: 13px;
  color: #555;
}

.video-section {
  margin: 30px 0;
  display: flex;
  justify-content: center;
}

.video-container {
  width: 100%;
  max-width: 800px;
}
</style>
